import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import {
  acceptInvite,
  acceptInviteViaEdgeFunction,
  previewInvite,
} from '@/lib/staff';
import type { InvitePreview } from '@/lib/types';
import { useIdentity } from '@/stores/identity';

/**
 * Public invite-acceptance page. URL: /invite/accept/:token
 *
 * Flow:
 *   1. Fetch invite preview by token (public RPC).
 *   2. If invalid/expired/revoked: show a clear error and a link home.
 *   3. If valid:
 *        a. Existing-user branch (already authenticated with the matching email):
 *           one-click "Accept" calls invite_accept(token).
 *        b. New-user branch: capture display name + password; call
 *           supabase.auth.signUp with raw_user_meta_data.invite_token so the
 *           handle_new_auth_user trigger skips org bootstrap; then call
 *           invite_accept(token, display_name).
 *   4. On success: refresh identity store and redirect to /.
 */
export default function InviteAccept() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const refreshIdentity = useIdentity((s) => s.refresh);

  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // We don't use useUser() here because identity store may be in 'error'
  // state when the auth session exists but there's no profile yet (the
  // orphan case: signup ran with invite_token, the bootstrap trigger
  // skipped, and the user got back to this page before invite_accept ran).
  // Source of truth for auth presence is supabase.auth.getSession().
  const [authedEmail, setAuthedEmail] = useState<string | null | undefined>(
    undefined
  );

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const p = await previewInvite(token);
        setPreview(p);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setAuthedEmail(data.session?.user.email ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Frame>
        <p className="text-sm text-muted-foreground">Loading invite...</p>
      </Frame>
    );
  }

  if (error) {
    return (
      <Frame title="Invite link error">
        <p className="text-sm text-destructive">{error}</p>
      </Frame>
    );
  }

  if (!preview || !preview.is_valid) {
    return (
      <Frame title="Invite link invalid">
        <p className="text-sm text-muted-foreground">
          {preview?.failure_reason === 'expired'
            ? 'This invite has expired. Ask whoever invited you to send a fresh link.'
            : 'This link is invalid, has been revoked, or was already used.'}
        </p>
      </Frame>
    );
  }

  // Wait for the session probe to settle before deciding which branch to render.
  if (authedEmail === undefined) {
    return (
      <Frame>
        <p className="text-sm text-muted-foreground">Loading...</p>
      </Frame>
    );
  }

  // Already authenticated (with or without a profile yet — the orphan case)
  if (authedEmail !== null) {
    const matches =
      preview.email &&
      authedEmail.toLowerCase() === preview.email.toLowerCase();
    return (
      <Frame title={`Join ${preview.org_name ?? 'your organization'}`}>
        <p className="text-sm text-muted-foreground">
          You're invited as <strong>{preview.app_role}</strong>.
        </p>
        {matches ? (
          <ExistingUserAcceptButton
            token={token!}
            displayNameRequired={
              // If preview is for the orphan case (signup happened, no profile),
              // we still need a display name. Ask for it; existing-profile case
              // can leave it empty.
              true
            }
            // Prefill with the staff row's current display_name so the invitee
            // can confirm or correct it. If they edit, invite_accept() syncs
            // staff.display_name and logs the rename in the audit payload.
            staffDisplayName={preview.staff_display_name}
            onDone={async () => {
              await refreshIdentity();
              navigate('/');
            }}
          />
        ) : (
          <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            You're signed in as <strong>{authedEmail}</strong>, but this
            invite is for <strong>{preview.email}</strong>. Sign out and open
            this link again, or paste it into a private window.
          </p>
        )}
      </Frame>
    );
  }

  return (
    <Frame title={`Join ${preview.org_name ?? 'your organization'}`}>
      <p className="text-sm text-muted-foreground">
        You're invited as <strong>{preview.app_role}</strong>. Create your
        account below — we'll set up the connection automatically.
      </p>
      <NewUserAcceptForm
        token={token!}
        invitedEmail={preview.email!}
        // Prefill the display-name field with the staff row's current name.
        // The invitee can edit; invite_accept() syncs staff.display_name on
        // change and writes the rename to the audit payload.
        staffDisplayName={preview.staff_display_name}
        onDone={async () => {
          await refreshIdentity();
          navigate('/');
        }}
      />
    </Frame>
  );
}

// ----- existing-user accept -------------------------------------------------

function ExistingUserAcceptButton({
  token,
  displayNameRequired = false,
  staffDisplayName,
  onDone,
}: {
  token: string;
  /**
   * If true, render a display-name input. Used for the orphan-session case
   * where the invite_accept must create a profile from scratch and needs a
   * display_name. Set false when we know the profile already exists.
   */
  displayNameRequired?: boolean;
  /**
   * Optional prefill for the display-name field, taken from the linked
   * staff row. Lets the invitee confirm/correct the name the manager
   * entered when they were added to the schedule.
   */
  staffDisplayName?: string | null;
  onDone: () => Promise<void>;
}) {
  const [displayName, setDisplayName] = useState(staffDisplayName ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onClick() {
    setError(null);
    if (displayNameRequired && !displayName.trim()) {
      setError('Please enter your name to finish setting up.');
      return;
    }
    setSubmitting(true);
    try {
      await acceptInvite(token, displayName.trim() || null);
      await onDone();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-2">
      {displayNameRequired && (
        <div className="space-y-1">
          <label
            htmlFor="invite_display_name"
            className="block text-sm font-medium"
          >
            Your name
          </label>
          <input
            id="invite_display_name"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className={inputClass}
          />
          <p className="text-xs text-muted-foreground">
            We'll use this on shifts and audit history.
          </p>
        </div>
      )}
      {error && (
        <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </p>
      )}
      <button
        type="button"
        onClick={onClick}
        disabled={submitting}
        className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
      >
        {submitting ? 'Accepting...' : 'Accept invite'}
      </button>
    </div>
  );
}

// ----- new-user accept form -------------------------------------------------

function NewUserAcceptForm({
  token,
  invitedEmail,
  staffDisplayName,
  onDone,
}: {
  token: string;
  invitedEmail: string;
  /**
   * Optional prefill for the display-name field, taken from the linked
   * staff row. Lets the invitee confirm/correct the name the manager
   * entered when they were added to the schedule. If they edit,
   * invite_accept() syncs staff.display_name and writes the rename to
   * the INVITE_ACCEPTED audit payload.
   */
  staffDisplayName?: string | null;
  onDone: () => Promise<void>;
}) {
  const [displayName, setDisplayName] = useState(staffDisplayName ?? '');
  const [password, setPassword] = useState('');
  // Show-password toggle, matching the SignUp page pattern. Reduces typo
  // risk on a long required password without trading away the masked
  // default.
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // Step 1: create the auth user.
      //
      // Preferred path: accept-staff-invite Edge Function uses the
      // service-role key to call auth.admin.createUser({email_confirm:true}),
      // which skips Supabase's default "Confirm your signup" email. The
      // invitee already proved email control by clicking the link in the
      // invite email, so a second confirmation email is redundant friction.
      //
      // Fallback path: if the Edge Function is down or errors for any
      // reason, fall back to plain supabase.auth.signUp(). The invitee
      // then gets the second confirmation email but signup still
      // completes. We never want invitees blocked by an EF blip.
      const trimmedName = displayName.trim();
      const efResult = await acceptInviteViaEdgeFunction({
        token,
        email: invitedEmail,
        password,
        displayName: trimmedName,
      });

      if (efResult.ok) {
        // EF created a confirmed user AND signed them in for us. Plant
        // the returned session into the supabase client so the next RPC
        // call (acceptInvite below) runs under the invitee's JWT --
        // invite_accept reads auth.uid() to know which profile to link.
        const { error: setErr } = await supabase.auth.setSession({
          access_token: efResult.accessToken,
          refresh_token: efResult.refreshToken,
        });
        if (setErr) throw setErr;
      } else {
        // non-critical: EF unavailable -- fall back to the classic signup
        // flow. Tracks the failure in console.warn so we can spot a
        // pattern (e.g. always-failing EF) in DevTools without surfacing
        // a confusing message to the invitee.
        console.warn(
          '[invite-accept] EF path unavailable, falling back to signUp:',
          efResult.error
        );
        const { data: signupData, error: signupErr } =
          await supabase.auth.signUp({
            email: invitedEmail,
            password,
            options: {
              data: {
                invite_token: token,
                display_name: trimmedName,
              },
            },
          });
        if (signupErr) throw signupErr;

        if (!signupData.session) {
          setError(
            'Check your email to verify your address, then return to this link to finish accepting the invite.'
          );
          setSubmitting(false);
          return;
        }
      }

      // Step 2: complete acceptance. With a session in place (either
      // route), invite_accept() runs under the new user's JWT so
      // auth.uid() resolves and the profile gets linked correctly.
      await acceptInvite(token, trimmedName);
      await onDone();
    } catch (err) {
      setError((err as Error).message);
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <div className="space-y-1">
        <label htmlFor="invite_email" className="block text-sm font-medium">
          Email
        </label>
        <input
          id="invite_email"
          type="email"
          value={invitedEmail}
          readOnly
          className={`${inputClass} bg-muted/50`}
        />
      </div>
      <div className="space-y-1">
        <label htmlFor="display_name" className="block text-sm font-medium">
          Your name
        </label>
        <input
          id="display_name"
          type="text"
          required
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          className={inputClass}
        />
      </div>
      <div className="space-y-1">
        <label htmlFor="password" className="block text-sm font-medium">
          Password
        </label>
        <div className="relative">
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            minLength={12}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputClass + ' pr-20'}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute inset-y-0 right-0 px-3 text-xs font-medium text-muted-foreground hover:text-foreground"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            aria-pressed={showPassword}
            // tabIndex=-1 keeps Tab going from the password field to the
            // submit button instead of detouring through the toggle —
            // most users won't reach for it, and screen readers can
            // still find it via the labels.
            tabIndex={-1}
          >
            {showPassword ? 'Hide' : 'Show'}
          </button>
        </div>
        <p className="text-xs text-muted-foreground">
          At least 12 characters with lowercase, uppercase, and a digit.
        </p>
      </div>

      {error && (
        <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
      >
        {submitting ? 'Working...' : 'Create account & accept'}
      </button>
    </form>
  );
}

// ----- frame ---------------------------------------------------------------

function Frame({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/20 px-4">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-8 space-y-4 shadow-sm">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold tracking-tight">
            {title ?? 'PharmaSync invite'}
          </h1>
          <p className="text-xs text-muted-foreground">PharmaSync</p>
        </div>
        {children}
      </div>
    </div>
  );
}

const inputClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';
